// Tile order for per-well arrays such as TileProjectionRegistrationSuccessRates
// and cell-paint TileValues. Molecule writes those arrays in
// Spatial.Run.wells[].tiles order, which InternalRunParameters.json stores on
// Wells[].Tiles. The flat Tiles list is a different walk (serpentine columns
// on OneWell) and must not be used as the index space.

export interface RunParameterTileRef {
  Name?: string;
}

export interface RunParameterWell {
  WellLocation?: string;
  Tiles?: Array<string | RunParameterTileRef>;
}

const tileName = (tile: string | RunParameterTileRef): string => {
  if (typeof tile === "string") return tile;
  return tile?.Name ?? "";
};

// Map of well location -> tile names in Wells[].Tiles order.
// Nameless entries are kept as "" so later indexes do not shift.
export const tilesByWellFromRunParameters = (
  wells: RunParameterWell[] | null | undefined
): Map<string, string[]> => {
  const result = new Map<string, string[]>();
  if (!Array.isArray(wells)) return result;

  wells.forEach((well) => {
    const location = well?.WellLocation;
    if (!location || !Array.isArray(well.Tiles)) return;
    result.set(
      location,
      well.Tiles.map((tile) => tileName(tile))
    );
  });

  return result;
};

// Assign rates[i] to the tile at Wells[].Tiles[i]. The first name wins if a
// tile is listed twice. Returns rates unchanged (callers scale to percent).
export const tileRatesByName = (
  wellTileNames: string[],
  rates: number[]
): Map<string, number> => {
  const valueMap = new Map<string, number>();
  const count = Math.min(wellTileNames.length, rates.length);
  for (let i = 0; i < count; i++) {
    const name = wellTileNames[i];
    if (!name || valueMap.has(name)) continue;
    valueMap.set(name, rates[i]);
  }
  return valueMap;
};
